// BHP-1160: the invoice detail page now shows the document Exact Online issued,
// not a view we assemble from our own rows.
//
// The line table, the totals and the dates that used to be here are gone. They
// were a second rendering of an invoice that already exists as a PDF, and the
// two could disagree on the cent (BHP-1026, BHP-1110) with no way for a reader
// to tell which one was the real invoice. Our own data is still the source for
// everything *around* the document — the status chip, the payment state — just
// not for the invoice itself.
//
// Tabs rather than one long page (PO decision, 2026-10-08): a specification can
// run to tens of pages, and only the tab in view fetches its document.
//
// The selected tab is marked with a background, not with the underline this
// kind of control usually gets. Measured in the running app: `border-0` plus
// `border-b-2` computes to `border-bottom-width: 0px` here, and so does the
// `border-0 border-b` idiom the migrated list pages use — their row separators
// are invisible too, and `ring-1` renders as a 0px shadow. Something in the
// pre-Preflight stylesheet order is eating those utilities (BHP-1077 territory,
// not this story). A background is the affordance that survives it.
//
// Drafts never reach this page: the backend keeps unfinalized invoices out of
// the portal entirely, so a draft uuid resolves to a 404 and lands on the
// overview like any other unreachable invoice.
import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  formatDate,
  formatMoney,
  formatMonth,
  type Invoice,
  type InvoiceSpecification,
  payableAmountOf,
  paymentLinkOf
} from '../../features/invoice/invoice';
import { PaymentStatusBadge } from '../../features/invoice/ui/PaymentStatusBadge';
import {
  useInvoice,
  useInvoiceDocument,
  useInvoiceSpecifications,
  useSpecificationDocument
} from '../../features/invoice/application/invoiceQueries';
import DocumentPanel from '../../features/invoice/ui/DocumentPanel';
import { ApiError } from '../../shared/http/ApiError';
import { toUserMessage } from '../../shared/http/errorMessage';

const tabId = (id: string): string => `invoice-tab-${id}`;
const panelId = (id: string): string => `invoice-panel-${id}`;

/** A uuid the active customer cannot reach resolves to nothing, not to an error. */
function isOutOfScope(error: unknown): boolean {
  return error instanceof ApiError && [403, 404].includes(error.status);
}

function invoiceFileName(invoice: Invoice): string {
  return `inv${invoice.invoiceNumber ?? invoice.uuid}.pdf`;
}

/**
 * BHP-1174: the way to pay this invoice, or nothing.
 *
 * `paymentUrl` is the whole decision, and deliberately so. A paid invoice, one
 * whose link is still being created, and an environment with Mollie switched
 * off (BHP-1181) all arrive here as `null` — the backend answers "can this be
 * paid", this page does not reconstruct that answer from a status. None of the
 * three is an error, so none of them says anything: a customer who cannot pay
 * here either has nothing to pay or will have the button in a minute.
 *
 * The amount is `amountInclVat` — the figure Exact issued the invoice for and
 * the one the Mollie link is built from — and never `totalInclVat`, which is
 * our own sum over our own lines and ignores the invoice discount by design
 * (BHP-1110). Naming it on the button is what makes it a decision rather than
 * a leap: nobody should discover what they are paying only after leaving the
 * portal, and least of all that it is a different number.
 *
 * Same tab, no `target`. Paying is the thing the customer came to do, not a
 * sidetrack, and a popup-blocked payment is worse than a lost scroll position.
 * Mollie brings them back by itself, to the public result page (BHP-1191).
 *
 * The question is asked through `paymentLinkOf()` and never by comparing the
 * field to null: the API leaves a null field out of the response entirely, so
 * `=== null` answers "there is a link" about an invoice that has none. That
 * mistake rendered an `<a>` with no `href` — a button on screen, inert on
 * click, on paid and unpaid invoices alike.
 */
function PayButton({ invoice }: { invoice: Invoice }): JSX.Element | null {
  const url = paymentLinkOf(invoice);

  if (url === null) {
    return null;
  }

  // No amount is better than the wrong one: an invoice can reach this page
  // without the figure Exact issued it for, and our own total is not a stand-in
  // for it.
  const amount = payableAmountOf(invoice);

  return (
    <a
      href={url}
      rel="noreferrer"
      className="rounded-md border-0 bg-teal-600 px-3 py-1.5 text-sm font-medium text-white no-underline hover:bg-teal-700"
    >
      {amount === null ? 'Pay' : `Pay ${formatMoney(amount, invoice.currency)}`}
    </a>
  );
}

function InvoiceTab({ invoice }: { invoice: Invoice }): JSX.Element {
  const {
    data: document,
    isPending,
    error
  } = useInvoiceDocument(invoice.uuid, invoice.documentState === 'available');

  return (
    <DocumentPanel
      state={invoice.documentState}
      document={document}
      isPending={isPending}
      error={error}
      fileName={invoiceFileName(invoice)}
      title={`Invoice ${invoice.invoiceNumber ?? ''}`.trim()}
    />
  );
}

function SpecificationTab({
  specification
}: {
  specification: InvoiceSpecification;
}): JSX.Element {
  const {
    data: document,
    isPending,
    error
  } = useSpecificationDocument(
    specification.uuid,
    specification.documentState === 'available'
  );

  return (
    <DocumentPanel
      state={specification.documentState}
      document={document}
      isPending={isPending}
      error={error}
      fileName={`specification-${specification.month}.pdf`}
      title={`Specification ${formatMonth(specification.month)}`}
    />
  );
}

export default function InvoiceDetails(): JSX.Element {
  const { uuid = null } = useParams<{ uuid: string }>();
  const [activeTab, setActiveTab] = useState<string>('invoice');

  const { data: invoice, isPending, isError, error } = useInvoice(uuid);
  const { data: specificationData, isError: specificationsFailed } =
    useInvoiceSpecifications(uuid);

  // Stale bookmark, an invoice of a customer you are no longer acting for, or
  // one that is not final: nothing to show and nothing broken.
  if (isOutOfScope(error)) {
    return <Navigate to="/finance/invoices/recent" replace />;
  }

  if (isPending) {
    return <p className="text-sm text-slate-500">Loading invoice…</p>;
  }

  if (isError || invoice === undefined) {
    return (
      <div
        role="alert"
        className="rounded-lg border border-solid border-rose-200 bg-rose-50 p-4 text-sm text-rose-700"
      >
        {toUserMessage(error, 'Could not load this invoice.')}
      </div>
    );
  }

  const specifications: InvoiceSpecification[] = specificationData?.items ?? [];
  // Every tab is named after the document behind it. Naming one by type and
  // the rest by month read as a date filter rather than as a second document.
  // The month stays because an invoice can carry one per billing month, and the
  // year with it: a January invoice can hold a December specification.
  const tabs = [
    { id: 'invoice', label: 'Invoice' },
    ...specifications.map((specification) => ({
      id: specification.uuid,
      label: `Specification · ${formatMonth(specification.month)}`
    }))
  ];
  const openSpecification = specifications.find(
    (specification) => specification.uuid === activeTab
  );

  function onTabKeyDown(event: React.KeyboardEvent<HTMLDivElement>): void {
    const step =
      event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    const jump =
      event.key === 'Home' ? 0 : event.key === 'End' ? tabs.length - 1 : null;

    if (step === 0 && jump === null) {
      return;
    }

    event.preventDefault();
    const current = tabs.findIndex((tab) => tab.id === activeTab);
    const next = jump ?? (current + step + tabs.length) % tabs.length;
    setActiveTab(tabs[next].id);
    document.getElementById(tabId(tabs[next].id))?.focus();
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {invoice.invoiceNumber === null
              ? `Invoice of ${formatDate(invoice.finalizedAt)}`
              : `Invoice ${invoice.invoiceNumber}`}
          </h2>
          <p className="mt-1 text-xs text-slate-400">
            {formatDate(invoice.finalizedAt)}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <PaymentStatusBadge invoice={invoice} />
          <PayButton invoice={invoice} />
        </div>
      </div>

      {specificationsFailed ? (
        <div
          role="alert"
          className="rounded-lg border border-solid border-amber-200 bg-amber-50 p-4 text-sm text-amber-800"
        >
          The specifications of this invoice could not be loaded. The invoice
          itself is shown below.
        </div>
      ) : null}

      {tabs.length > 1 ? (
        <div
          role="tablist"
          aria-label="Documents of this invoice"
          onKeyDown={onTabKeyDown}
          className="inline-flex flex-wrap gap-1 rounded-lg bg-slate-100 p-1"
        >
          {tabs.map((tab) => (
            <button
              key={tab.id}
              id={tabId(tab.id)}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              aria-controls={panelId(tab.id)}
              // Roving tabindex: one stop for the whole set, arrow keys move
              // between them. Without it a keyboard reader tabs through every
              // document before reaching the one on screen.
              tabIndex={activeTab === tab.id ? 0 : -1}
              onClick={() => {
                setActiveTab(tab.id);
              }}
              className={`cursor-pointer rounded-md border-0 px-3 py-1.5 text-sm font-medium ${
                activeTab === tab.id
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'bg-transparent text-slate-500 hover:text-slate-800'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      ) : null}

      <div
        role="tabpanel"
        id={panelId(activeTab)}
        aria-labelledby={tabs.length > 1 ? tabId(activeTab) : undefined}
        tabIndex={0}
      >
        {openSpecification === undefined ? (
          <InvoiceTab invoice={invoice} />
        ) : (
          <SpecificationTab specification={openSpecification} />
        )}
      </div>
    </div>
  );
}
